import { Component } from '@angular/core';
import { Subject, switchMap, takeUntil } from 'rxjs';
import Swal from 'sweetalert2';
import { IProblemsDetailed } from '../../../../../../interfaces/Problems/IProblemsDetailed';
import { ResolvedProblemsService } from '../../../../../../services/resolvedProblems.service';
import { ResolvedProblemsAddEditComponent } from './resolved-problems-add-edit/resolved-problems-add-edit.component';
import { ResolvedProblemsExpandComponent } from './resolved-problems-expand/resolved-problems-expand.component';
import { TranslateService } from '@ngx-translate/core';
import { SwalToastService } from 'src/app/services/swal.service';
import { PermissionService } from 'src/app/services/permission.service';
import { CurrentProblemsService } from 'src/app/services/currentProblems.service';
import { LocaleService } from 'src/app/services/locale.service';

@Component({
    selector: 'app-resolved-problems',
    templateUrl: './resolved-problems.component.html',
    styleUrls: ['./resolved-problems.component.css'],
    standalone: false
})
export class ResolvedProblemsComponent {
  public addComp = ResolvedProblemsAddEditComponent;
  public expandComp = ResolvedProblemsExpandComponent;

  private destroy$ = new Subject<void>();

  resolvedProblems?: IProblemsDetailed[];
  isLoading: boolean = false;

  constructor(
    private readonly resolvedProblemsService: ResolvedProblemsService,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService,
    private readonly permissionService: PermissionService,
    private readonly currentProblemsService: CurrentProblemsService,
    private readonly localeService: LocaleService,
  ) {}

  ngOnInit() {
    this.resolvedProblemsService.resolvedProblemLoadingSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: boolean) => {
        this.isLoading = data;
      });

    this.resolvedProblemsService.resolvedProblemDataSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: IProblemsDetailed[]) => {
        this.resolvedProblems = data;
      });

    this.localeService.locale$
      .pipe(
        switchMap(() => this.resolvedProblemsService.loadData()),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  hasPermission(permission: string[]): boolean {
    return this.permissionService.can(permission);
  }

  openModalFun(item?: IProblemsDetailed) {
    this.resolvedProblemsService.resolvedProblemModalSub.next(true);
    this.resolvedProblemsService.resolvedProblemEditSub.next(item);
  }

  deleteFun(resolvedProblemId: number) {
    Swal.fire({
      text: `${this.translate.instant(
        'deletePlaceholder1'
      )} ${this.translate.instant('Resolved / Inactive Problem')}?`,
      showDenyButton: false,
      showCancelButton: true,
      cancelButtonText: `${this.translate.instant('Cancel')}`,
      confirmButtonText: `${this.translate.instant('Ok')}`,
    }).then((result) => {
      if (result.isConfirmed) {
        this.resolvedProblemsService
          .deleteProblems(resolvedProblemId.toString())
          .subscribe({
            next: (d) => {
              this.swalToastService.toastSuccess(
                'Resolved Problem deleted successfully'
              );
              this.resolvedProblemsService.deleteResolvedProblemFromTable(
                resolvedProblemId.toString()
              );
            },
            error: (err) => {
              this.swalToastService.toastError(
                'Unable to delete resolved problem'
              );
            },
          });
      } else if (result.isDenied) {
        Swal.fire(
          `${this.translate.instant('Resolved Problem is not deleted')}`,
          '',
          'info'
        );
      }
    });
  }

  revertStopFun(medicationId: number) {
    Swal.fire({
      text: `${this.translate.instant(
        'revertPlaceholder1'
      )} ${this.translate.instant('Problem')}?`,
      showDenyButton: false,
      showCancelButton: true,
      cancelButtonText: `${this.translate.instant('Cancel')}`,
      confirmButtonText: `${this.translate.instant('Ok')}`,
    }).then((result) => {
      if (result.isConfirmed) {
        this.resolvedProblemsService
          .deletePastProblem(medicationId.toString())
          .subscribe({
            next: (d) => {
              this.swalToastService.toastSuccess(
                'Problem reverted successfully'
              );
              this.currentProblemsService.getData();
              this.resolvedProblemsService.getData();
            },
            error: (err) => {
              this.swalToastService.toastError('Unable to revert Problem');
            },
          });
      } else if (result.isDenied) {
        Swal.fire(
          `${this.translate.instant('Problem is not reverted')}`,
          '',
          'info'
        );
      }
    });
  }
}
